import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Activity, TrendingUp, Users, Package, AlertTriangle, 
  CheckCircle, Clock, RefreshCw, Zap, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const StatusDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    fetchDashboard();
    // Auto-refresh every 15 seconds
    const interval = setInterval(() => {
      fetchDashboard();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/trc/dashboard/status-board`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDashboard(response.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !dashboard) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: ZOBOX_BLUE }}></div>
          <p className="text-gray-600">Loading Status Board...</p>
        </div>
      </div>
    );
  }

  const statusBoard = dashboard.status_board;
  const engineerStats = dashboard.engineer_stats;
  const todayActivity = dashboard.today_activity;

  const totalDevices = Object.values(statusBoard).reduce((sum, count) => sum + count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Activity className="w-8 h-8 mr-3" />
            Real-Time Status Dashboard
          </h1>
          <p className="text-gray-600 mt-1 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-green-500" />
            Live - Updated {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={fetchDashboard}
          className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: ZOBOX_BLUE }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Now
        </button>
      </div>

      {/* Today's Activity */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4 flex items-center">
          <Target className="w-6 h-6 mr-2" />
          Today's Activity
        </h2>
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <p className="text-sm opacity-90">Devices Received</p>
            <p className="text-4xl font-bold mt-2">{todayActivity.devices_received}</p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <p className="text-sm opacity-90">Repairs Completed</p>
            <p className="text-4xl font-bold mt-2">{todayActivity.repairs_completed}</p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <p className="text-sm opacity-90">FQC Completed</p>
            <p className="text-4xl font-bold mt-2">{todayActivity.fqc_completed}</p>
          </div>
        </div>
      </div>

      {/* Engineer Utilization */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center" style={{ color: ZOBOX_BLUE }}>
          <Users className="w-6 h-6 mr-2" />
          Engineer Capacity
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">Total Engineers</p>
            <p className="text-3xl font-bold" style={{ color: ZOBOX_BLUE }}>
              {engineerStats.total_engineers}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Total Capacity</p>
            <p className="text-3xl font-bold text-gray-800">
              {engineerStats.total_capacity}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Assigned</p>
            <p className="text-3xl font-bold text-orange-600">
              {engineerStats.total_assigned}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Available</p>
            <p className="text-3xl font-bold text-green-600">
              {engineerStats.available_capacity}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Utilization</p>
            <p className={`text-3xl font-bold ${
              engineerStats.utilization_percentage > 80 ? 'text-red-600' :
              engineerStats.utilization_percentage > 60 ? 'text-orange-600' :
              'text-green-600'
            }`}>
              {engineerStats.utilization_percentage}%
            </p>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full transition-all ${
                engineerStats.utilization_percentage > 80 ? 'bg-red-500' :
                engineerStats.utilization_percentage > 60 ? 'bg-orange-500' :
                'bg-green-500'
              }`}
              style={{ width: `${engineerStats.utilization_percentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Status Flow Board */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-6 flex items-center" style={{ color: ZOBOX_BLUE }}>
          <Package className="w-6 h-6 mr-2" />
          Device Status Flow ({totalDevices} Total Devices)
        </h2>
        
        {/* Flow visualization */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {/* Received */}
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 text-center">
            <div className="text-blue-600 text-sm font-medium mb-2">Received</div>
            <div className="text-3xl font-bold text-blue-700">{statusBoard.received}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.received / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* Binned */}
          <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 text-center">
            <div className="text-purple-600 text-sm font-medium mb-2">Binned</div>
            <div className="text-3xl font-bold text-purple-700">{statusBoard.binned}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.binned / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* IQC Pending */}
          <div className="bg-indigo-50 border-2 border-indigo-300 rounded-lg p-4 text-center">
            <div className="text-indigo-600 text-sm font-medium mb-2">IQC Pending</div>
            <div className="text-3xl font-bold text-indigo-700">{statusBoard.iqc_pending}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.iqc_pending / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* Ready for Repair */}
          <div className="bg-cyan-50 border-2 border-cyan-300 rounded-lg p-4 text-center">
            <div className="text-cyan-600 text-sm font-medium mb-2">Ready for Repair</div>
            <div className="text-3xl font-bold text-cyan-700">{statusBoard.rfr}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.rfr / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* Assigned */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 text-center">
            <div className="text-yellow-600 text-sm font-medium mb-2">Assigned</div>
            <div className="text-3xl font-bold text-yellow-700">{statusBoard.assigned}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.assigned / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* In Repair */}
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 text-center">
            <div className="text-orange-600 text-sm font-medium mb-2">In Repair</div>
            <div className="text-3xl font-bold text-orange-700">{statusBoard.in_repair}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.in_repair / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* Repair Completed */}
          <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-4 text-center">
            <div className="text-teal-600 text-sm font-medium mb-2">Repair Completed</div>
            <div className="text-3xl font-bold text-teal-700">{statusBoard.repair_completed}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.repair_completed / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* FQC Pass */}
          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 text-center">
            <div className="text-green-600 text-sm font-medium mb-2 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              FQC Pass
            </div>
            <div className="text-3xl font-bold text-green-700">{statusBoard.fqc_pass}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.fqc_pass / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* FQC Fail */}
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 text-center">
            <div className="text-red-600 text-sm font-medium mb-2">FQC Fail (RTR)</div>
            <div className="text-3xl font-bold text-red-700">{statusBoard.fqc_fail}</div>
            <div className="text-xs text-gray-600 mt-1">
              {((statusBoard.fqc_fail / totalDevices) * 100).toFixed(1)}%
            </div>
          </div>

          {/* SOS Priority */}
          <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4 text-center">
            <div className="text-red-700 text-sm font-medium mb-2 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              SOS Priority
            </div>
            <div className="text-3xl font-bold text-red-800">{statusBoard.sos_count}</div>
            <div className="text-xs text-gray-600 mt-1">URGENT</div>
          </div>

          {/* BER */}
          <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4 text-center">
            <div className="text-gray-600 text-sm font-medium mb-2">BER</div>
            <div className="text-3xl font-bold text-gray-700">{statusBoard.ber}</div>
            <div className="text-xs text-gray-600 mt-1">Not Repairable</div>
          </div>

          {/* ASC Repair */}
          <div className="bg-pink-50 border-2 border-pink-300 rounded-lg p-4 text-center">
            <div className="text-pink-600 text-sm font-medium mb-2">ASC Repair</div>
            <div className="text-3xl font-bold text-pink-700">{statusBoard.asc_repair}</div>
            <div className="text-xs text-gray-600 mt-1">External</div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(statusBoard.sos_count > 0 || engineerStats.utilization_percentage > 80) && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-red-600 mt-1 mr-3" />
            <div>
              <h3 className="text-lg font-bold text-red-800">Action Required</h3>
              <ul className="mt-2 space-y-1 text-red-700">
                {statusBoard.sos_count > 0 && (
                  <li>• {statusBoard.sos_count} devices marked as SOS priority need immediate attention</li>
                )}
                {engineerStats.utilization_percentage > 80 && (
                  <li>• Engineer capacity at {engineerStats.utilization_percentage}% - Consider additional resources</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Auto-refresh indicator */}
      <div className="text-center text-sm text-gray-500">
        <Clock className="w-4 h-4 inline mr-2" />
        Auto-refreshing every 15 seconds
      </div>
    </div>
  );
};

export default StatusDashboard;
